<template>
  <div class="router-wrapper">
    <div class="element-wrapper">
      <products-search-modal
        :products="products"
        :addProducts="addProducts"
        :alreadyAddedCustomers="(this.report.products || []).map(p => p.customer)"
      />
      <div class="os-tabs-w">
        <div class="os-tabs-controls os-tabs-complex">
          <div class="nav-item">
            <h1 v-if="isConfiguring">{{ $t("reports_create.configure_heading") }}</h1>
            <h1 v-else>{{ $t("reports_create.heading") }}</h1>
          </div>
          <div>
            <button
              type="button"
              class="button"
              color="primary"
              size="xlarge"
              v-on:click="send"
              :disabled="isSavingReport"
            >
              {{ $t("general.save") }}
            </button>
          </div>
        </div>
      </div>

      <div class="content-box">
        <div class="element-box">
          <h5 class="form-header">
            {{ $t("reports_create.report_data") }}
          </h5>
          <div class="form-desc">
            {{ $t("reports_create.report_data_description") }}
          </div>
          <edit-report-info :report="report" :can-edit-report-number="!isConfiguring" />
        </div>

        <div class="element-box">
          <div class="row" style="border-bottom: 1px solid rgba(0, 0, 0, 0.05)">
            <div class="col-6">
              <h5 class="form-header">
                {{ $t("general.products") }}
                <span v-if="report.products && report.products.length > 0">({{ report.products.length }})</span>
              </h5>
              <div class="form-desc" style="border-bottom: none; margin-bottom: 0px">
                {{ $t("reports_create.select_products") }}
              </div>
            </div>

            <div class="col-6" style="padding-bottom: 20px" v-if="USER_ROLE === 'admin'">
              <h6 class="form-header">
                {{ $t("reports_create.bulk_import_measurements") }}
              </h6>
              <bulk-import-meassurements
                @clear="
                  () => {
                    this.onBulkClear();
                  }
                "
                :selectedProducts="report.products"
                :fileName="bulkImportFileName"
                @t="$t"
                @importSuccess="
                  fileName => {
                    this.onBulkImportSuccess(fileName);
                  }
                "
              />
            </div>
          </div>

          <div class="row mt-5" :key="updater">
            <div class="col-12">
              <div class="support-index show-ticket-content show-ticket-content-assign-templates">
                <div class="support-tickets">
                  <div class="support-tickets-header">
                    <div class="tickets-filter">
                      <button
                        type="button"
                        class="button"
                        color="primary"
                        size="large"
                        data-toggle="modal"
                        data-target="#products-search-modal"
                      >
                        {{ $t("reports_create.add_product") }}
                      </button>
                    </div>
                    <div
                      v-for="(product, index) in report.products"
                      v-bind:key="`${product.id}-${index}`"
                      class="support-ticket product-ticket"
                      v-bind:class="{
                        active: product.id == selectedProduct.id && product.sheetId == selectedProduct.sheetId,
                        'has-error': !product.isValid
                      }"
                      v-on:click="selectProduct(product)"
                    >
                      <div class="st-body">
                        <div class="avatar template-error" v-show="!product.isValid">
                          <i class="fas fa-exclamation"></i>
                        </div>
                        <div class="ticket-content">
                          <h6 class="ticket-title">
                            {{ product.description }}
                          </h6>
                          <div v-if="product.selectedTemplate">
                            {{ $t("report.checked_with") }}:
                            {{ product.selectedTemplate.name }}
                          </div>
                        </div>
                        <div style="cursor: pointer; color: #e65252; right: 12px; position: absolute; top: 12px">
                          <i
                            class="far fa-trash-alt"
                            style="cursor: pointer"
                            v-on:click="removeProduct(product)"
                            v-if="!isDeletingSheet"
                          ></i>
                          <clip-loader :loading="isDeletingSheet" :size="'20px'"></clip-loader>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="support-ticket-content-w">
                  <div class="support-ticket-content" v-if="selectedProduct && selectedProduct.id">
                    <div class="support-ticket-content-header">
                      <h3 class="ticket-header">{{ selectedProduct.description }} #{{ selectedProduct.sheetId }}</h3>
                    </div>
                    <div class="ticket-thread element-wrapper support-tickets-create-report">
                      <!-- DATA -->
                      <h6 class="element-header">
                        {{ $t("general.data") }}
                      </h6>

                      <div>
                        <div class="row">
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.article_no") }}</label>
                              <input
                                v-if="selectedProduct.properties.ARTICLE_NUMBER"
                                value=""
                                v-model="selectedProduct.properties.ARTICLE_NUMBER.value"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.surface") }}</label>
                              <input
                                v-model="selectedProduct.properties.SURFACE_COATING.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.head") }}</label>
                              <input
                                v-model="selectedProduct.properties.HEAD.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.head_stamp") }}</label>
                              <input
                                v-model="selectedProduct.properties.HEAD_STAMP.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.recess") }}</label>
                              <input
                                v-model="selectedProduct.properties.RECESS_TYPE.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.recess_size") }}</label>
                              <input
                                v-model="selectedProduct.properties.RECESS_SIZE.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                        </div>

                        <div class="row">
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.thread_type") }}</label>
                              <input
                                v-model="selectedProduct.properties.THREAD_TYPE.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.point") }}</label>
                              <input
                                v-model="selectedProduct.properties.POINT.name"
                                class="form-control"
                                type="text"
                                disabled
                              />
                            </div>
                          </div>
                        </div>
                        <div class="row">
                          <div class="col-sm-6">
                            <div class="form-group">
                              <label>{{ $t("report.drawing_no") }}</label>
                              <div v-if="selectedProduct.properties.DRAWING_NUMBER">
                                <input
                                  v-model="selectedProduct.properties.DRAWING_NUMBER.value"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                              <div v-else>
                                <input class="form-control" type="text" disabled />
                              </div>
                            </div>
                          </div>
                        </div>

                        <edit-report-sheet-info :report-sheet="selectedProduct" />

                        <div v-if="selectedProduct.woodInfo">
                          <div class="row">
                            <div class="col-sm-6">
                              <div class="form-group">
                                <label>{{ $t("report.wood_information.wood_no") }}</label>
                                <input
                                  v-model="selectedProduct.woodInfo.woodNr"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                            </div>

                            <div class="col-sm-6">
                              <div class="form-group">
                                <label>{{ $t("report.wood_information.wood_moisture") }}</label>
                                <input
                                  v-model="selectedProduct.woodInfo.woodMoisture"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                            </div>
                          </div>

                          <div class="row">
                            <div class="col-sm-6">
                              <div class="form-group">
                                <label>{{ $t("report.wood_information.wood_density") }}</label>
                                <input
                                  v-model="selectedProduct.woodInfo.woodDensity"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                            </div>

                            <div class="col-sm-6">
                              <div class="form-group">
                                <label>{{ $t("report.wood_information.room_temperature") }}</label>
                                <input
                                  v-model="selectedProduct.woodInfo.roomTemperature"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                            </div>
                          </div>

                          <div class="row">
                            <div class="col-sm-6">
                              <div class="form-group">
                                <label>{{ $t("report.wood_information.room_moisture") }}</label>
                                <input
                                  v-model="selectedProduct.woodInfo.roomMoisture"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                            </div>

                            <div class="col-sm-6">
                              <div class="form-group">
                                <label>{{ $t("report.wood_information.insertion_direction") }}</label>
                                <input
                                  v-model="selectedProduct.woodInfo.insertionDirection"
                                  class="form-control"
                                  type="text"
                                  disabled
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div style="height: 170px">
                        <!-- TEMPLATES -->
                        <h6 class="element-header">
                          {{ $t("reports_create.select_test_template") }}
                        </h6>

                        <div class="support-tickets">
                          <div
                            class="support-ticket"
                            v-for="template in selectedProduct.templates"
                            v-bind:class="{
                              'is-card-valid': selectedProduct.selectedTemplate
                                ? template.id == selectedProduct.selectedTemplate.id
                                : false
                            }"
                            v-on:click="selectTemplate(template)"
                            v-bind:key="template.name"
                          >
                            <div class="st-body">
                              <div class="ticket-content">
                                <h6 class="ticket-title">
                                  {{ template.name }}
                                </h6>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div v-if="selectedProduct.selectedTemplate && USER_ROLE === 'admin'">
                        <!-- IMPORT measurements -->
                        <h6 class="element-header">
                          {{ $t("reports_create.import_measurements") }}
                          <i
                            class="fas fa-exclamation"
                            style="color: orange"
                            v-if="
                              selectedProduct.measurements &&
                                selectedProduct.measurements.filter(m => !m.existsOnTemplate).length > 0
                            "
                          ></i>
                        </h6>

                        <div class="container">
                          <import-meassurements
                            @t="$t"
                            :characteristics="
                              selectedProduct.selectedTemplate.productTemplateCharacteristics ||
                                selectedProduct.selectedTemplate.productCharacteristics
                            "
                            :data="selectedProduct.measurements"
                            :fileName="selectedProduct.fileName"
                            @clear="
                              () => {
                                this.onClearMeasurements(selectedProduct);
                              }
                            "
                            @importSuccess="
                              (data, fileName) => {
                                this.onImportSuccess(selectedProduct, data, fileName);
                              }
                            "
                          />

                          <div class="row" style="width: 100%">
                            <import-out-table
                              :data="selectedProduct.measurements"
                              v-if="selectedProduct.measurements"
                              style="width: 100%"
                              @t="$t"
                            ></import-out-table>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div v-else></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- MODAL -->

        <div class="row mt-5">
          <div class="col-lg-12">
            <button
              type="button"
              class="button"
              color="primary"
              size="max"
              v-on:click="send"
              :disabled="isSavingReport"
            >
              {{ $t("general.save") }}
            </button>
          </div>
        </div>

        <simplert :useRadius="true" :useIcon="true" ref="simplert"> </simplert>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import MaskedInput from "vue-masked-input";
import ClipLoader from "vue-spinner/src/ClipLoader.vue";
import {
  ImportMeasurements,
  ImportOutTable
} from "@/components/react/components/import-meassurments/import-meassurements";
import BulkImportMeasurements from "@/components/react/components/import-meassurments/bulk-import-meassurements";
import { ReactInVue } from "vuera";
import Datepicker from "vuejs-datepicker";
import * as lang from "vuejs-datepicker/src/locale";
import moment from "moment";
import ReactDatePicker from "react-datepicker";
import EditReportInfo from "@/components/reports/partials/show/edit/edit-report-info";
import EditReportSheetInfo from "@/components/reports/partials/show/edit/edit-report-sheet-info.vue";
import ProductsSearchModal from "@/components/products/search/products-search.modal";
import * as lodash from "lodash";
import { parseToNumber } from "@/utils/number";

export default {
  name: "ReportsCreate",
  props: {
    isConfiguring: {
      default: false,
      type: Boolean
    },
    initial_report: {
      default: null,
      type: Object
    }
  },
  data() {
    return {
      sheet_date_backup: null,
      USER_ROLE: "",
      languages: lang,
      isDeletingSheet: false,
      report: {
        report_number: null,
        name: null,
        order_confirmation: null,
        charge: null,
        customer_order_number: null,
        shipping_date: null,
        order_date: null,
        customer: null,
        manufacturer: null,
        production_plant: null,
        material_certificate: null,
        heat_nr: null,
        products: [],
        work_order_nr: null
      },
      products: [],
      selectedProduct: {
        selectedTemplate: null
      },
      reportNumberRegex: RegExp(/([A-Z]+)-([0-9]{2})-([0-9]{5})/),
      isCheckingReportNumber: false,
      reportNumberError: null,
      nextReportNumber: null,
      previousReportNumber: null,
      updater: null,
      bulkImportFileName: null,
      report_prefix: null,
      isSavingReport: false
    };
  },
  components: {
    EditReportInfo,
    MaskedInput,
    ClipLoader,
    "import-meassurements": ReactInVue(ImportMeasurements),
    "bulk-import-meassurements": ReactInVue(BulkImportMeasurements),
    "import-out-table": ReactInVue(ImportOutTable),
    "react-date-picker": ReactInVue(ReactDatePicker),
    Datepicker,
    ProductsSearchModal,
    EditReportSheetInfo
  },
  methods: {
    ...mapActions({
      fetchProducts: "productStore/fetchAsync",
      fetchDetail: "productStore/fetchDetail",
      save: "reportStore/save",
      checkReportNumber: "reportStore/checkReportNumber",
      deleteSheetFromReport: "reportStore/deleteSheetFromReport"
    }),
    addProducts(products, selectedTemplate, materialCertificate, heatNr, batch) {
      Promise.all(products.map(p => this.fetchDetail(p.id))).then(detailProducts => {
        detailProducts.forEach((p, index) => {
          const product = Object.assign({}, p);
          product.selectedTemplate = selectedTemplate;
          product.order_confirmation = null;
          product.shipping_date = null;
          product.material_certificate = materialCertificate;
          product.charge = batch;
          product.customer_order_number = null;
          product.heat_nr = heatNr;
          product.customer = p.customer;
          product.orderQuantity = products.find(p1 => p1.id === p.id).orderQuantity;

          if (product.selectedTemplate) {
            product.selectedTemplate = product.templates.find(t => t.id === product.selectedTemplate.id);
          }

          if (!selectedTemplate && product.templates && product.templates.length === 1) {
            // if no template was selected and the product only has one template, assign this
            product.selectedTemplate = product.templates[0];
          }

          const samples = products.find(p1 => p1.id === p.id).sheetIdOfSamples || 10;
          let num = parseInt(samples / 10, 10);
          if (samples % 10 !== 0) {
            num++;
          }

          for (let i = 0; i < num; i++) {
            this.report.products.push({
              ...product,
              sheetId: (lodash.max(this.report.products.map(s => lodash.parseInt(s.sheetId))) || 0) + 1
            });
          }

          this.sortProducts();
        });
      });

      $("#selectProductModal").modal("hide");
    },
    sortProducts() {
      this.report.products.forEach(s => {
        s.screwDiameter = parseToNumber(`${s.screwDiameter}`.replace(/[^0-9,.]/g, ""));
        s.screwLength = parseToNumber(`${s.screwLength}`.replace(/[^0-9,.]/g, ""));
      });
      this.report.products = lodash.sortBy(
        this.report.products,
        "customer",
        "productGroup.name",
        "screwType",
        "head",
        "recessType",
        "screwDiameter",
        "screwLength",
        "threadType",
        "surfaceColor"
      );
    },
    selectProduct(product) {
      if (product.sheetId) {
        this.selectedProduct = product;
      }
    },
    selectTemplate(template) {
      this.selectedProduct.selectedTemplate = template;
      this.evalMeasurements(this.selectedProduct);
    },
    send() {
      this.$validator.validateAll().then(valid => {
        if (valid && this.report.isValid) {
          this.isSavingReport = true;
          this.save(this.report, this.report.id ? this.report.id : null)
            .then(response => {
              this.isSavingReport = false;
              this.$router.push("/reports");
            })
            .catch(e => {
              this.isSavingReport = false;
              alert(e);
            });
        } else {
          console.log("not valid");
        }
      });
    },
    useReportNumber(num) {
      this.isCheckingReportNumber = false;
      this.nextReportNumber = null;
      this.reportNumberError = false;
      this.report.report_number = num;
    },
    onConfirmImportSingle(onConfirmFunc) {
      const obj = {
        title: this.$i18n.t("general.confirm"),
        message: this.$i18n.t("reports_create.overwrite_dialog.message"),
        type: "warning",
        customCloseBtnText: this.$i18n.t("general.cancel"),
        customConfirmBtnText: this.$i18n.t("general.overwrite"),
        useConfirmBtn: true,
        customConfirmBtnClass: "button-red",
        onConfirm: onConfirmFunc
      };
      this.$refs.simplert.openSimplert(obj);
    },
    onImportSuccess(product, data, fileName) {
      // const rProduct = this.report.products.find(p => p.id === product.id)
      const importData = () => {
        product.measurements = data;
        product.fileName = fileName;

        this.updater = this.updater + 1;
      };

      if (product.hasMeasurements) {
        this.onConfirmImportSingle(importData);
      } else {
        importData();
      }
    },
    onConfirmBulkOverwrite(overwritten, onConfirmFunc) {
      const overwrittenText = overwritten.map(o => `<strong>${o.articleNumber}</strong> #${o.sheetId} </br>`).join("");

      const obj = {
        title: this.$i18n.t("general.confirm"),
        message: this.$i18n.t("reports_create.bulk_overwrite_dialog.message", {
          overwrittenText: overwrittenText
        }),
        type: "warning",
        customCloseBtnText: this.$i18n.t("general.cancel"),
        customConfirmBtnText: this.$i18n.t("general.overwrite"),
        useConfirmBtn: true,
        customConfirmBtnClass: "button-red",
        onConfirm: onConfirmFunc
      };
      this.$refs.simplert.openSimplert(obj);
    },
    onBulkImportSuccess(fileName) {
      this.bulkImportFileName = fileName;
      this.report.products.forEach(p => this.evalMeasurements(p));

      // check if measurements would be overwritten
      const overwritten = [];
      this.report.products.forEach(product => {
        if (product.new_measurements && product.new_measurements && product.hasMeasurements) {
          overwritten.push({
            articleNumber: product.articleNumber,
            number: product.sheetId
          });
        } else if (product.new_measurements) {
          product.measurements = product.new_measurements;
          product.new_measurements = undefined;
          this.evalMeasurements(product);
        }
      });

      if (overwritten.length > 0) {
        this.onConfirmBulkOverwrite(overwritten, () => {
          this.report.products.forEach(p => {
            if (p.new_measurements) {
              p.measurements = p.new_measurements;
              p.new_measurements = undefined;

              this.evalMeasurements(p);
            }
          });
        });
      }

      this.updater = this.updater + 1;
    },
    evalMeasurements(product) {
      if (product.selectedTemplate && product.measurements) {
        product.measurements.forEach(m => {
          const search =
            product.selectedTemplate.productCharacteristics ||
            product.selectedTemplate.productTemplateCharacteristics ||
            [];
          const characteristic = search.find(c => c.shortcut === m.characteristic);
          m.existsOnTemplate = !!characteristic;
          m.id = characteristic ? characteristic.id : undefined;
        });
      }
    },
    onClearMeasurements(product) {
      product.measurements = null;
      product.fileName = null;
      this.updater = this.updater + 1;
    },
    onBulkClear() {
      this.report.products.forEach(product => {
        product.measurements = undefined;
        product.new_measurements = undefined;
      });

      this.bulkImportFileName = null;
    },
    removeProduct(product) {
      const remove = () => {
        this.report.products = this.report.products.filter(p => p.sheetId !== product.sheetId);

        product.sheetId = null;

        if (this.selectedProduct.sheetId === product.sheetId) {
          this.selectedProduct.id = null;
        }

        this.report.products.forEach((p, index) => {
          p.sheetId = index + 1;
        });
      };

      if (product.existed) {
        const obj = {
          title: this.$i18n.t("general.confirm"),
          message: this.$i18n.t("reports_create.delete_dialog.message"),
          type: "warning",
          customCloseBtnText: this.$i18n.t("general.cancel"),
          customConfirmBtnText: this.$i18n.t("general.delete"),
          useConfirmBtn: true,
          customConfirmBtnClass: "button-red",
          onConfirm: () => {
            this.isDeletingSheet = true;
            this.deleteSheetFromReport({
              reportId: this.report.id,
              sheetId: product.sheetId
            })
              .then(() => {
                this.isDeletingSheet = false;
                this.$notify({
                  title: this.$t("general.success"),
                  text: this.$t("reports_create.delete_dialog.success"),
                  type: "success"
                });
                remove();
              })
              .catch(e => {
                console.error(e);
                this.isDeletingSheet = false;
                this.$notify({
                  title: this.$t("general.success"),
                  text: this.$t("reports_create.delete_dialog.error"),
                  type: "error"
                });
              });
          }
        };
        this.$refs.simplert.openSimplert(obj);
      } else {
        remove();
      }
    },
    isDatesValid() {
      return moment(this.report.shipping_date).isValid(); /*&& moment(this.report.order_date).isValid();*/
    }
  },

  mounted() {
    this.USER_ROLE = JSON.parse(sessionStorage.getItem("cedoon_user")).role;
    this.report_prefix = JSON.parse(sessionStorage.getItem("cedoon_user")).report_number_prefix;
    if (this.initial_report) {
      // this.report = this.initial_report
      this.report.report_number = this.initial_report.report_number;
      this.report.name = this.initial_report.name;
      this.report.order_confirmation = this.initial_report.order_confirmation;
      this.report.charge = this.initial_report.charge;
      this.report.customer_order_number = this.initial_report.customer_order_number;
      this.report.shipping_date = moment(this.initial_report.shipping_date).toDate();
      this.report.order_date = this.initial_report.order_date
        ? moment(this.initial_report.order_date).toDate()
        : undefined;
      this.report.work_order_nr = this.initial_report.work_order_nr;
      this.report.customer = this.initial_report.customer;
      this.report.manufacturer = this.initial_report.manufacturer;
      this.report.production_plant = this.initial_report.production_plant;
      this.report.material_certificate = this.initial_report.material_certificate;
      this.report.heat_nr = this.initial_report.heat_nr;
      this.report.id = this.initial_report.id;
      this.report.products = this.initial_report.sheets.map((sheet, index) => {
        return {
          ...sheet,
          ...sheet.product,
          selectedTemplate: sheet.template || sheet.product.templates.find(t => t.id === sheet.template_id),
          number: index + 1,
          existed: true,
          sheetId: sheet.id
        };
      });
      this.$validator.validateAll();
    }

    this.sortProducts();

    this.fetchProducts()
      .then(response => {
        this.products = response.data.products;
      })
      .catch(e => alert(e));
  },
  watch: {
    report: {
      deep: true,
      handler() {
        for (const product of this.report.products) {
          product.isValid = product.selectedTemplate != null;
        }

        this.report.isValid =
          this.isDatesValid() &&
          this.report.products.length > 0 &&
          this.report.products.filter(p => !p.isValid).length === 0;
      }
    }
  }
};
</script>

<style scoped></style>
